import React, { useEffect, useState } from "react";
import { api, STAGE_LABELS } from "../lib/api";
import { useGameSocket } from "../lib/useGameSocket";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BingoCard, CalledBoard, completeRows } from "../components/BingoBits";
import { TicketLookup } from "../components/TicketLookup";
import { AdminNav } from "../components/AdminNav";
import { toast, Toaster } from "sonner";
import { Play, Pause, Undo2, Check, X, Power, ArrowRight, Timer } from "lucide-react";

export default function Host() {
  const [pin, setPin] = useState(localStorage.getItem("host_pin") || "");
  const [authed, setAuthed] = useState(false);
  const [gameId, setGameId] = useState(null);
  const [inputNum, setInputNum] = useState("");
  const [pendingClaim, setPendingClaim] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [autoDraw, setAutoDraw] = useState(false);
  const [autoSeconds, setAutoSeconds] = useState(5);

  useEffect(() => {
    if (pin) setAuthed(true);
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/games/current").then((r) => setGameId(r.data?.id || null));
  }, [authed]);

  const { game, viewers } = useGameSocket(gameId, (msg) => {
    if (msg.type === "bingo_claim") {
      toast.error(`BINGO CLAIM! ${msg.claim.display_name}`, { duration: 8000 });
      try { new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=").play(); } catch (e) { /* ignore */ }
    }
    if (msg.type === "bingo_claim_polled") toast.error("BINGO CLAIM! Controleer de kaart", { duration: 8000 });
    if (msg.type === "claim_verified") {
      toast(msg.approve ? `${msg.display_name} wint ${STAGE_LABELS[msg.stage]}!` : "Claim afgewezen");
    }
  });

  useEffect(() => {
    if (game?.pending_claim_id) {
      api.get(`/claims/${game.pending_claim_id}`).then((r) => setPendingClaim(r.data)).catch(() => {});
    } else {
      setPendingClaim(null);
    }
  }, [game?.pending_claim_id]);

  const call = async (path, extra = {}) => {
    try {
      const r = await api.post(path, { pin, ...extra });
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout");
    }
  };

  const drawNumber = async (n) => {
    if (!game) return;
    if (game.status !== "live") { toast.error("Start eerst het spel"); return; }
    if (n < 1 || n > 90) { toast.error("Ongeldig nummer"); return; }
    await call(`/games/${gameId}/draw`, { number: n });
    setInputNum("");
  };

  useEffect(() => {
    if (!autoDraw || !game || game.status !== "live") return;
    const iv = setInterval(() => {
      const left = Array.from({ length: 90 }, (_, i) => i + 1).filter((n) => !(game.drawn || []).includes(n));
      if (!left.length) { setAutoDraw(false); return; }
      call(`/games/${gameId}/draw`, { number: left[Math.floor(Math.random() * left.length)] });
    }, Math.max(2, autoSeconds) * 1000);
    return () => clearInterval(iv);
  }, [autoDraw, autoSeconds, game?.status, game?.drawn?.length, gameId]);

  const submitPin = async () => {
    if (!pin) return;
    try {
      await api.post("/auth/pin", { pin });
      localStorage.setItem("host_pin", pin);
      setAuthed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ongeldige PIN");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-6">
        <Toaster theme="dark" position="top-center" richColors />
        <div className="w-full max-w-sm p-8 rounded-2xl bg-[#12141D] border border-white/10">
          <h1 className="text-2xl font-black text-slate-100 mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>Presentator Login</h1>
          <p className="text-sm text-slate-400 mb-6">Voer de PIN in om te bedienen.</p>
          <Input data-testid="host-pin-input" type="password" placeholder="PIN" value={pin} onChange={(e) => setPin(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submitPin()} className="bg-[#0B0C10] border-white/10 text-white" />
          <Button data-testid="host-pin-submit" onClick={submitPin} className="mt-4 w-full bg-[#FFD700] text-[#0B0C10] hover:bg-[#E6C200]">Inloggen</Button>
        </div>
      </div>
    );
  }

  if (!gameId || !game || game.status === "finished") {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-6 text-center text-slate-300" data-testid="host-no-game">
        <div>
          <div className="text-xl font-bold text-slate-100">{game?.status === "finished" ? `"${game.title}" is beëindigd` : "Geen actief spel"}</div>
          <div className="mt-2 text-sm">Maak een nieuwe bingo-avond aan via het beheerscherm.</div>
          <a href="/admin" data-testid="host-goto-admin" className="inline-block mt-5 px-5 py-3 rounded-xl bg-[#FFD700] text-[#0B0C10] font-black">Naar /admin →</a>
        </div>
      </div>
    );
  }

  const drawn = game.drawn || [];
  const drawnSet = new Set(drawn);

  return (
    <div className="min-h-screen bg-[#0B0C10] text-slate-100 p-4 lg:p-8">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-7xl mx-auto space-y-6">
        <AdminNav />
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Presentator Dashboard</div>
            <h1 className="text-3xl font-black" style={{ fontFamily: "Unbounded, sans-serif" }}>{game.title}</h1>
          </div>
          <div className="flex gap-2 items-center">
            <span data-testid="host-viewers" className="px-3 py-1.5 rounded-lg bg-[#12141D] border border-white/10 text-sm">Kijkers: <b className="text-[#00F0FF]">{viewers}</b></span>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
              game.status === "live" ? "bg-[#00E676]/20 text-[#00E676]" :
              game.status === "paused" ? "bg-[#FFD700]/20 text-[#FFD700]" :
              "bg-white/10 text-slate-300"
            }`} data-testid="host-status">{game.status.toUpperCase()}</span>
            <span className="px-3 py-1.5 rounded-lg bg-[#FF007F]/20 text-[#FF007F] text-sm font-bold">{STAGE_LABELS[game.stage]}</span>
          </div>
        </header>

        {/* Controls */}
        <div className="flex flex-wrap gap-2">
          {game.status === "lobby" && (
            <Button data-testid="btn-start" onClick={() => call(`/games/${gameId}/start`)} className="bg-[#00E676] text-[#0B0C10] hover:bg-[#00c765]">
              <Play className="w-4 h-4 mr-2" /> Start Spel
            </Button>
          )}
          {game.status === "live" && (
            <Button data-testid="btn-pause" onClick={() => call(`/games/${gameId}/pause`)} variant="outline" className="border-white/10 bg-[#12141D] text-slate-100 hover:bg-[#1F2833]">
              <Pause className="w-4 h-4 mr-2" /> Pauze
            </Button>
          )}
          {game.status === "paused" && !pendingClaim && (
            <Button data-testid="btn-resume" onClick={() => call(`/games/${gameId}/resume`)} className="bg-[#00E676] text-[#0B0C10] hover:bg-[#00c765]">
              <Play className="w-4 h-4 mr-2" /> Hervat
            </Button>
          )}
          <Button data-testid="btn-undo" onClick={() => call(`/games/${gameId}/undo`)} variant="outline" className="border-white/10 bg-[#12141D] text-slate-100 hover:bg-[#1F2833]">
            <Undo2 className="w-4 h-4 mr-2" /> Herstel laatste
          </Button>
          {game.stage !== "full" && (
            <Button data-testid="btn-next-stage" onClick={() => call(`/games/${gameId}/next-stage`)} variant="outline" className="border-white/10 bg-[#12141D] text-slate-100 hover:bg-[#1F2833]">
              <ArrowRight className="w-4 h-4 mr-2" /> Volgende ronde
            </Button>
          )}
          <Button data-testid="btn-end" onClick={() => setConfirmEnd(true)} variant="outline" className="ml-auto border-[#FF1744]/40 bg-[#FF1744]/10 text-[#FF1744] hover:bg-[#FF1744]/20">
            <Power className="w-4 h-4 mr-2" /> Beëindig avond
          </Button>
        </div>

        {confirmEnd && (
          <div className="p-5 rounded-2xl bg-[#FF1744]/10 border border-[#FF1744]/40 flex flex-wrap items-center justify-between gap-3" data-testid="confirm-end">
            <div>
              <div className="font-bold text-[#FF1744]">Hele bingo-avond beëindigen?</div>
              <div className="text-sm text-slate-300">Dit sluit het spel definitief af. Kijkers kunnen dan niet meer meespelen. Wil je alleen even stoppen, gebruik dan <b>Pauze</b>.</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="confirm-end-cancel" variant="outline" onClick={() => setConfirmEnd(false)} className="border-white/10 bg-[#12141D] text-slate-100 hover:bg-[#1F2833]">Annuleren</Button>
              <Button data-testid="confirm-end-yes" onClick={() => { setConfirmEnd(false); call(`/games/${gameId}/end`); }} className="bg-[#FF1744] text-white hover:bg-[#d3143a]">Ja, beëindig</Button>
            </div>
          </div>
        )}

        {/* Draw input */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 p-5 rounded-2xl bg-[#12141D] border border-white/10">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Nummer invoeren</div>
            <div className="flex gap-2">
              <Input data-testid="draw-input" inputMode="numeric" type="number" min="1" max="90" placeholder="1-90"
                     value={inputNum} onChange={(e) => setInputNum(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && drawNumber(parseInt(inputNum, 10))}
                     className="bg-[#0B0C10] border-white/10 text-3xl font-black h-16 text-center" />
              <Button data-testid="draw-submit" onClick={() => drawNumber(parseInt(inputNum, 10))} className="h-16 bg-[#FFD700] text-[#0B0C10] hover:bg-[#E6C200] font-black">
                TREK
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Type het getrokken balletje en druk Enter.</p>

            <div className={`mt-5 p-3 rounded-xl border ${autoDraw ? "border-[#00F0FF]/50 bg-[#00F0FF]/10" : "border-white/10 bg-[#0B0C10]"}`} data-testid="autodraw-panel">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><Timer className="w-3.5 h-3.5" /> Oefenstand</div>
                <Button data-testid="autodraw-toggle" size="sm" onClick={() => setAutoDraw((v) => !v)}
                        className={autoDraw ? "bg-[#00F0FF] text-[#0B0C10] hover:bg-[#00d4e0]" : "bg-white/10 text-slate-100 hover:bg-white/20"}>
                  {autoDraw ? "Stop" : "Automatisch trekken"}
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-300">
                elke
                <Input data-testid="autodraw-seconds" type="number" min={2} max={60} value={autoSeconds}
                       onChange={(e) => setAutoSeconds(Number(e.target.value) || 5)}
                       className="w-16 h-8 bg-[#12141D] border-white/10 text-white text-center" />
                seconden een willekeurig balletje{autoDraw && game.status !== "live" && <span className="text-[#FFD700]"> — wacht tot het spel LIVE is</span>}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 p-5 rounded-2xl bg-[#12141D] border border-white/10">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Snel klikken (1-90)</div>
            <div className="grid grid-cols-10 gap-1">
              {Array.from({ length: 90 }, (_, i) => i + 1).map((n) => {
                const called = drawnSet.has(n);
                return (
                  <button key={n} data-testid={`quick-${n}`} onClick={() => drawNumber(n)} disabled={called || game.status !== "live"}
                          className={`aspect-square rounded-md text-xs font-bold transition ${
                            called ? "bg-[#FFD700] text-[#0B0C10]" : "bg-[#1F2833] hover:bg-[#FFD700]/30 text-slate-300 disabled:opacity-40"
                          }`}>
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Board & Claim */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-400">Bord</div>
            <CalledBoard drawn={drawn} />
          </div>

          <div className="space-y-6">
            <div className="p-5 rounded-2xl bg-[#12141D] border border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Bingo Verificatie</div>
              {!pendingClaim ? (
                <div className="text-slate-500 text-sm">Geen openstaande claim.</div>
              ) : (
              <div>
                <div className="mb-3">
                  <div className="text-lg font-bold" data-testid="claim-name">{pendingClaim.display_name}</div>
                  <div className="text-sm text-slate-400">Claimt: <b className="text-slate-200">{STAGE_LABELS[pendingClaim.stage]}</b></div>
                  <div className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${pendingClaim.auto_valid ? "bg-[#00E676]/20 text-[#00E676]" : "bg-[#FF1744]/20 text-[#FF1744]"}`}
                       data-testid="claim-auto">
                    {pendingClaim.auto_valid ? "AUTO-CHECK: GELDIG" : "AUTO-CHECK: ONGELDIG"}
                  </div>
                </div>
                <BingoCard card={pendingClaim.card} drawn={drawn} winRows={completeRows(pendingClaim.card, drawn)} />
                <p className="text-xs text-slate-400 mt-2" data-testid="claim-rows-info">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#00E676] mr-1.5 align-middle" />
                  Groen = volledige rij ({completeRows(pendingClaim.card, drawn).length}/3 rijen vol)
                </p>
                <div className="flex gap-2 mt-4">
                  <Button data-testid="verify-approve" onClick={() => call("/claims/verify", { claim_id: pendingClaim.id, approve: true })}
                          className="flex-1 bg-[#00E676] text-[#0B0C10] hover:bg-[#00c765]">
                    <Check className="w-4 h-4 mr-2" /> Goedkeuren
                  </Button>
                  <Button data-testid="verify-reject" onClick={() => call("/claims/verify", { claim_id: pendingClaim.id, approve: false })}
                          className="flex-1 bg-[#FF1744] text-white hover:bg-[#e0143a]">
                    <X className="w-4 h-4 mr-2" /> Afwijzen
                  </Button>
                </div>
              </div>
            )}
            </div>
            <TicketLookup gameId={gameId} pin={pin} drawn={drawn} />
          </div>
        </div>
      </div>
    </div>
  );
}
